import {
  Check,
  Info,
  Tag,
  Layers,
  Calculator,
  ShieldCheck,
  Zap,
} from "lucide-react";
import PayrollComponentToolsBar from "./PayrollComponentToolsBar";
import {
  CATEGORY_OPTIONS,
  CALC_OPTIONS,
  categoryMeta,
  isVariablePayrollComponent,
  isOvertimePayrollComponent,
  overlayOvertimeFromExtraWork,
  variableComponentMeta,
  isCodeLocked,
} from "./salaryComponentModel";

function Field({ label, hint, children, htmlFor }) {
  return (
    <div className="scp-field">
      <label className="scp-field-label" htmlFor={htmlFor}>
        {label}
      </label>
      {children}
      {hint ? <p className="scp-field-hint">{hint}</p> : null}
    </div>
  );
}

function EditorSection({ icon: Icon, title, description, children }) {
  return (
    <section className="scp-editor-section">
      <header className="scp-editor-section-head">
        <span className="scp-editor-section-icon" aria-hidden>
          <Icon size={18} />
        </span>
        <div>
          <h3 className="scp-editor-section-title">{title}</h3>
          {description ? <p className="scp-editor-section-desc">{description}</p> : null}
        </div>
      </header>
      <div className="scp-editor-section-body">{children}</div>
    </section>
  );
}

/**
 * Single-page component create/edit form (Basics, Category, Calculation, Tax & Statutory).
 */
export default function ComponentEditorForm({
  form,
  setForm,
  mode = "create",
  initialRow = null,
  componentId = null,
  extraWork = null,
  onOpenExtraWork,
  submitted = false,
}) {
  const set = (patch) => setForm((p) => ({ ...p, ...patch }));

  const isVariable = isVariablePayrollComponent(initialRow || form);
  const displayRow = overlayOvertimeFromExtraWork(initialRow || form, extraWork);
  const variableInfo = variableComponentMeta(displayRow);
  const overtimeSynced = isOvertimePayrollComponent(displayRow);
  const otMethod = displayRow?.meta?.extra_work_policy?.method_label || extraWork?.overtime?.method || "";
  const codeLocked = isCodeLocked(initialRow || form);

  const showFormula = form.calculation_type === "formula" && !isVariable;
  const showSystem = form.calculation_type === "system" && !isVariable;

  return (
    <div className="scp-editor-form">
      <EditorSection
        icon={Tag}
        title="Basics"
        description="Unique code and name used on payslips and in formulas."
      >
        {isVariable && variableInfo ? (
          <div className="scp-variable-panel">
            <div className="scp-variable-panel-head">
              <Zap size={16} />
              <strong>{variableInfo.typeLabel}</strong>
            </div>
            <dl className="scp-variable-dl">
              <div><dt>Type</dt><dd>{variableInfo.typeLabel}</dd></div>
              <div><dt>Category</dt><dd>{categoryMeta(variableInfo.category).label}</dd></div>
              <div><dt>Code</dt><dd><code>{variableInfo.code}</code></dd></div>
              <div><dt>Source</dt><dd>{variableInfo.source}</dd></div>
              <div><dt>Calculation</dt><dd>{variableInfo.calculation}</dd></div>
              <div><dt>Payroll behavior</dt><dd>{variableInfo.payrollBehavior}</dd></div>
            </dl>
            <p className="scp-variable-help">{variableInfo.help}</p>
            {overtimeSynced ? (
              <div className="scp-ot-sync-box">
                <p>
                  Calculation is owned by Extra Work, Premium Pay &amp; OT.
                  {otMethod ? <> Current method: <strong>{otMethod}</strong>.</> : null}
                  {" "}Calendar overtime and extra-time days that pay OT are one earning — not two allocations.
                </p>
                {typeof onOpenExtraWork === "function" ? (
                  <button type="button" className="scp-guide-step-cta" onClick={onOpenExtraWork}>
                    {variableInfo.settingsLabel || "Open Extra Work, Premium Pay & OT"}
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        <div className="scp-grid-2">
          <Field label="Code" htmlFor="ed-code" hint="Uppercase, stable identifier used by formulas.">
            <input
              id="ed-code"
              className={`bw-input${submitted && !form.code.trim() ? " invalid" : ""}`}
              value={form.code}
              placeholder="BASIC"
              disabled={codeLocked}
              onChange={(e) => set({ code: e.target.value })}
            />
          </Field>
          <Field label="Name" htmlFor="ed-name" hint="Human-friendly label shown on payslips.">
            <input
              id="ed-name"
              className={`bw-input${submitted && !form.name.trim() ? " invalid" : ""}`}
              value={form.name}
              placeholder="Basic Salary"
              onChange={(e) => set({ name: e.target.value })}
            />
          </Field>
        </div>
        <Field label="Description (optional)" htmlFor="ed-desc" hint="A short note about what this component represents.">
          <input
            id="ed-desc"
            className="bw-input"
            value={form.description}
            placeholder="Core fixed pay"
            onChange={(e) => set({ description: e.target.value })}
          />
        </Field>
      </EditorSection>

      <EditorSection
        icon={Layers}
        title="Category"
        description="Whether this adds to pay, reduces it, or is an employer-side cost."
      >
        <div className="scp-cat-grid">
          {CATEGORY_OPTIONS.map((o) => {
            const m = categoryMeta(o.value);
            const active = form.component_category === o.value;
            return (
              <button
                type="button"
                key={o.value}
                className={`scp-cat-card scp-tone-${m.tone} ${active ? "active" : ""}`}
                onClick={() => !isVariable && set({ component_category: o.value })}
                disabled={isVariable}
              >
                <span className="scp-cat-dot" />
                <span className="scp-cat-name">{m.label}</span>
                <span className="scp-cat-hint">{o.hint}</span>
                {active ? <Check size={16} className="scp-cat-check" /> : null}
              </button>
            );
          })}
        </div>
        {form.component_category === "earning" ? (
          <Field label="Attendance proration mode" htmlFor="ed-apm" hint="Controls whether LOP / attendance reduces this earning.">
            <select
              id="ed-apm"
              className="bw-select"
              value={form.attendance_proration_mode}
              onChange={(e) => set({ attendance_proration_mode: e.target.value })}
            >
              <option value="">Default (auto when template prorate on)</option>
              <option value="auto">Auto</option>
              <option value="manual">Manual</option>
              <option value="disabled">Disabled</option>
            </select>
          </Field>
        ) : null}
      </EditorSection>

      <EditorSection
        icon={Calculator}
        title="Calculation"
        description={
          isVariable
            ? "System-defined amounts during payroll."
            : "Fixed amount, formula, or statutory engine."
        }
      >
        {!isVariable ? (
          <PayrollComponentToolsBar
            activePage="edit"
            componentId={componentId || initialRow?.component_id}
            componentCode={form.code || initialRow?.code || ""}
          />
        ) : null}
        {isVariable ? (
          <div className="scp-info-note">
            <Info size={15} />
            <span>
              {overtimeSynced
                ? "Overtime is not a fixed salary line. Change the rate, extra-time methods, and eligibility on Extra Work, Premium Pay & OT — this component displays the same policy and payroll uses it once."
                : "Variable payroll components are not assigned as fixed salary amounts. Overtime and Advance Taken appear on the payslip only when the employee has overtime or a pending salary advance for the period."}
            </span>
          </div>
        ) : (
          <>
            <div className="scp-calc-grid">
              {CALC_OPTIONS.map((o) => {
                const active = form.calculation_type === o.value;
                return (
                  <button
                    type="button"
                    key={o.value}
                    className={`scp-calc-card ${active ? "active" : ""}`}
                    onClick={() => set({ calculation_type: o.value })}
                  >
                    <span className="scp-calc-name">
                      {o.label}
                      {active ? <Check size={15} /> : null}
                    </span>
                    <span className="scp-calc-hint">{o.hint}</span>
                  </button>
                );
              })}
            </div>
            {showFormula ? (
              <Field label="Formula expression" htmlFor="ed-formula" hint="Reference other component codes, e.g. BASIC * 0.4. You can also assign this later in Formula Builder.">
                <input
                  id="ed-formula"
                  className={`bw-input scp-mono${submitted && !form.formula_expression.trim() ? " invalid" : ""}`}
                  value={form.formula_expression}
                  placeholder="BASIC * 0.4"
                  onChange={(e) => set({ formula_expression: e.target.value })}
                />
              </Field>
            ) : null}
            {showSystem ? (
              <Field label="System code" htmlFor="ed-system" hint="Links to the statutory engine, e.g. PF, ESI, PT, TDS.">
                <input
                  id="ed-system"
                  className={`bw-input scp-mono${submitted && !form.system_code.trim() ? " invalid" : ""}`}
                  value={form.system_code}
                  placeholder="PF"
                  onChange={(e) => set({ system_code: e.target.value })}
                />
              </Field>
            ) : null}
          </>
        )}
      </EditorSection>

      <EditorSection
        icon={ShieldCheck}
        title="Tax & status"
        description="Rounding, activation, and statutory mapping notes."
      >
        <div className="scp-grid-2">
          <Field label="Rounding scale" htmlFor="ed-round" hint="Decimal places used when rounding the computed amount.">
            <input
              id="ed-round"
              type="number"
              min="0"
              className="bw-input"
              value={form.rounding_rule?.scale ?? 2}
              onChange={(e) => set({ rounding_rule: { ...(form.rounding_rule || {}), scale: Number(e.target.value) } })}
            />
          </Field>
          <Field label="Status" htmlFor="ed-active" hint="Inactive components are hidden from new payroll assignments.">
            <select
              id="ed-active"
              className="bw-select"
              value={form.is_active ? "yes" : "no"}
              onChange={(e) => set({ is_active: e.target.value === "yes" })}
            >
              <option value="yes">Active</option>
              <option value="no">Inactive</option>
            </select>
          </Field>
        </div>
        <div className="scp-info-note">
          <Info size={15} />
          <span>
            Detailed statutory mapping (PF / ESI / PT / TDS rules) is managed on the <strong>Statutory</strong> tab.
            Statutory-type components link there automatically via their system code.
          </span>
        </div>
      </EditorSection>
    </div>
  );
}
